import { GridApi } from "ag-grid-community";
import {
  BudgetCategory,
  BudgetContingency,
  BudgetFringe,
  BudgetFringeUnit,
  BudgetItem,
  BudgetSubCategory,
} from "./types";
import { instanceOfBudgetSubCategory } from "./TopSheetBudgetGrid";
import {
  budgetItemQuantityInHours,
  calculateBudgetFringeTotals,
  roundBudgetMoney,
  type BudgetFringeAmounts,
  type BudgetFringeEntry,
  type BudgetFringeInput,
} from "./budgetFringeTotals";

export const toNullableBudgetNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getBudgetItemEstimate = (budgetItem: BudgetItem): number => {
  // Children replace the parent total (no parent+child double-count).
  if (budgetItem.children && budgetItem.children.length > 0) {
    return budgetItem.children.reduce((sum, child) => sum + getBudgetItemEstimate(child), 0);
  }

  const qty = Number(budgetItem.quantity);
  const rate = Number(budgetItem.rate);
  if (!qty || !rate) return 0;
  let itemEstimate = qty * rate;
  if (budgetItem.x_factor) {
    itemEstimate *= budgetItem.x_factor;
  }
  return itemEstimate;
};

export const getBudgetItemsEstimate = (budgetItems: BudgetItem[]): number => {
  return budgetItems.reduce((sum, budgetItem) => {
    return sum + getBudgetItemEstimate(budgetItem);
  }, 0);
};

export const getBudgetItemsEstimateIncludingFringes = (budgetItems: BudgetItem[]): number => {
  const budgetEstimate = getBudgetItemsEstimate(budgetItems);
  const fringes = getFringesFromBudgetItems(budgetItems);
  return budgetEstimate + fringes.estimate;
};

export const getBudgetItemActual = (budgetItem: BudgetItem): number => {
  // Children replace every financial field on a parent account. Ignoring a
  // legacy parent actual keeps UI totals aligned with the backend summary.
  if (budgetItem.children && budgetItem.children.length > 0) {
    return budgetItem.children.reduce((sum, child) => sum + getBudgetItemActual(child), 0);
  }

  const actual = Number(budgetItem.actual_amount);
  return Number.isFinite(actual) ? actual : 0;
};

export const getBudgetItemsActual = (budgetItems: BudgetItem[]): number => {
  return budgetItems.reduce((sum, budgetItem) => sum + getBudgetItemActual(budgetItem), 0);
};

/** Estimate minus actuals, including the full estimate when no actual has been entered. */
export const getBudgetItemRemaining = (budgetItem: BudgetItem): number => {
  return getBudgetItemEstimate(budgetItem) - getBudgetItemActual(budgetItem);
};

export const getBudgetItemsRemaining = (budgetItems: BudgetItem[]): number => {
  return getBudgetItemsEstimate(budgetItems) - getBudgetItemsActual(budgetItems);
};

export const getBudgetItemVariance = (budgetItem: BudgetItem): number => {
  const actual = getBudgetItemActual(budgetItem);
  return actual ? getBudgetItemEstimate(budgetItem) - actual : 0;
};

export const getBudgetItemsVariance = (budgetItems: BudgetItem[]): number => {
  return budgetItems.reduce((sum, budgetItem) => sum + getBudgetItemVariance(budgetItem), 0);
};

export const getAllItemsInSubCategories = (
  budgetSubCategories: BudgetSubCategory[]
): BudgetItem[] => {
  return ([] as BudgetItem[]).concat(
    ...budgetSubCategories.map((subCategory) => subCategory.items)
  );
};

const getFringeEntry = (
  item: BudgetItem,
  categoryIndex: number,
  fringes: BudgetFringe[] | undefined
): BudgetFringeEntry => ({
  categoryIndex,
  estimate: getBudgetItemEstimate(item),
  actual: getBudgetItemActual(item),
  quantityInHours: budgetItemQuantityInHours(item),
  fringes,
});

const getFringeEntries = (itemsByCategory: BudgetItem[][]): BudgetFringeEntry[] =>
  itemsByCategory.flatMap((items, categoryIndex) =>
    items.flatMap((item) => {
      // A linked parent already charges its fringe on the children's total, so a child
      // linked to the same fringe adds nothing.
      const parentFringeIds = new Set(
        (item.fringes ?? []).map((fringe) => fringe.budget_fringe_id)
      );
      return [
        getFringeEntry(item, categoryIndex, item.fringes),
        ...(item.children ?? []).map((child) =>
          getFringeEntry(
            child,
            categoryIndex,
            child.fringes?.filter((fringe) => !parentFringeIds.has(fringe.budget_fringe_id))
          )
        ),
      ];
    })
  );

export const getBudgetFringeTotals = (categories: BudgetCategory[]) =>
  calculateBudgetFringeTotals(
    getFringeEntries(categories.map(getAllItemsInCategory)),
    categories.length
  );

const getBudgetCategoryBaseEstimate = (category: BudgetCategory): number =>
  roundBudgetMoney(
    category.sub_categories.reduce(
      (sum, subcategory) => sum + roundBudgetMoney(getBudgetItemsEstimate(subcategory.items)),
      0
    )
  );

export const getBudgetCategoryEstimate = (
  category: BudgetCategory,
  allCategories: BudgetCategory[] = [category]
): number => {
  const index = allCategories.findIndex(
    (candidate) => candidate.budget_category_id === category.budget_category_id
  );
  const fringes = getBudgetFringeTotals(allCategories).categories[index]?.estimate ?? 0;
  return roundBudgetMoney(getBudgetCategoryBaseEstimate(category) + fringes);
};

export const getBudgetCategoriesEstimate = (categories: BudgetCategory[]): number =>
  roundBudgetMoney(
    categories.reduce((sum, category) => sum + getBudgetCategoryBaseEstimate(category), 0) +
      getBudgetFringeTotals(categories).estimate
  );

export const getBudgetCategoryActual = (
  category: BudgetCategory,
  allCategories: BudgetCategory[] = [category]
): number => {
  const index = allCategories.findIndex(
    (candidate) => candidate.budget_category_id === category.budget_category_id
  );
  const actual = category.sub_categories.reduce(
    (sum, subcategory) => sum + roundBudgetMoney(getBudgetItemsActual(subcategory.items)),
    0
  );
  return roundBudgetMoney(
    actual + (getBudgetFringeTotals(allCategories).categories[index]?.actual ?? 0)
  );
};

/** Line variances plus this category's share of the budget's fringes, like its fringe row. */
export const getBudgetCategoryVariance = (
  category: BudgetCategory,
  allCategories: BudgetCategory[] = [category]
): number => {
  const index = allCategories.findIndex(
    (candidate) => candidate.budget_category_id === category.budget_category_id
  );
  const fringes = getBudgetFringeTotals(allCategories).categories[index];
  const fringeVariance = fringes?.actual ? roundBudgetMoney(fringes.estimate - fringes.actual) : 0;
  return getBudgetItemsVariance(getAllItemsInCategory(category)) + fringeVariance;
};

export const getAllItemsInCategory = (budgetCategory: BudgetCategory): BudgetItem[] => {
  return ([] as BudgetItem[]).concat(
    ...budgetCategory.sub_categories.map((subCategory: BudgetSubCategory) => subCategory.items)
  );
};

export const getAllItemsInCategories = (budgetCategories: BudgetCategory[]): BudgetItem[] => {
  return ([] as BudgetItem[]).concat(
    ...budgetCategories.flatMap((category) => getAllItemsInCategory(category))
  );
};

export const getBudgetContingencyFromGrid = (gridApi: GridApi): BudgetContingency | null => {
  // AG Grid excludes pinned rows from forEachNode. Prefer the saved footer row.
  const pinnedCount = gridApi.getPinnedBottomRowCount?.() ?? 0;
  for (let index = 0; index < pinnedCount; index += 1) {
    const data = gridApi.getPinnedBottomRow?.(index)?.data;
    if (instanceOfBudgetContingency(data)) return data;
  }

  let budgetContingency: BudgetContingency | null = null;
  gridApi.forEachNode((node) => {
    if (instanceOfBudgetContingency(node.data)) budgetContingency = node.data;
  });
  return budgetContingency;
};

export const getBudgetTotalsFromGrid = (gridApi: GridApi) => {
  const categories: BudgetCategory[] = [];
  gridApi.forEachNode((node) => {
    const data = node.data;
    if (data && typeof data === "object" && "sub_categories" in data) categories.push(data);
  });
  const contingency = getBudgetContingencyFromGrid(gridApi);
  const estimate = getBudgetTotalEstimate(categories, contingency);
  const actual = getBudgetTotalActual(categories, contingency);
  return { estimate, actual, variance: roundBudgetMoney(estimate - actual) };
};

export const getLockedSubCategoriesFromGrid = (gridApi: GridApi) => {
  const lockedSubCategories: BudgetSubCategory[] = [];
  gridApi.forEachNode((node: any) => {
    if (instanceOfBudgetSubCategory(node.data) && node.data.locked) {
      lockedSubCategories.push(node.data);
    }
  });
  return lockedSubCategories;
};

export const getBudgetEstimateContingency = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => {
  const budgetTotalEstimateIncludingFringes = getBudgetCategoriesEstimate(budgetCategories);
  return roundBudgetMoney(
    (budgetTotalEstimateIncludingFringes * (budgetContingency?.contingency_percentage ?? 0)) / 100
  );
};

export const getBudgetContingencyVariance = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => {
  return budgetContingency?.actual_amount
    ? getBudgetEstimateContingency(budgetCategories, budgetContingency) -
        budgetContingency.actual_amount
    : 0;
};

export const getBudgetTotalEstimate = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => {
  const budgetTotalEstimateIncludingFringes = getBudgetCategoriesEstimate(budgetCategories);
  const budgetEstimateContingency = getBudgetEstimateContingency(
    budgetCategories,
    budgetContingency
  );
  return roundBudgetMoney(budgetTotalEstimateIncludingFringes + budgetEstimateContingency);
};

/** Prefer explicit budget_section; fall back to name/account heuristics (not category position). */
export const isAboveTheLineCategory = (category: BudgetCategory): boolean => {
  if (category.budget_section === "atl") return true;
  if (category.budget_section === "btl") return false;

  const name = category.name.trim().toLowerCase();
  if (name.includes("above the line") || name.includes("above-the-line")) return true;
  if (name.includes("below the line") || name.includes("below-the-line") || /\bbtl\b/.test(name)) {
    return false;
  }

  for (const subCategory of category.sub_categories) {
    const match = subCategory.account_number?.trim().match(/^(\d+)/);
    if (match) {
      const accountNumber = Number.parseInt(match[1], 10);
      if (Number.isFinite(accountNumber) && accountNumber < 2000) return true;
    }
  }

  return false;
};

export const getAboveTheLineEstimate = (budgetCategories: BudgetCategory[]): number => {
  return roundBudgetMoney(
    budgetCategories
      .filter(isAboveTheLineCategory)
      .reduce((sum, category) => sum + getBudgetCategoryEstimate(category, budgetCategories), 0)
  );
};

export const getAboveTheLineActual = (budgetCategories: BudgetCategory[]): number => {
  return roundBudgetMoney(
    budgetCategories
      .filter(isAboveTheLineCategory)
      .reduce((sum, category) => sum + getBudgetCategoryActual(category, budgetCategories), 0)
  );
};

export const getBelowTheLineEstimate = (budgetCategories: BudgetCategory[]): number => {
  return roundBudgetMoney(
    budgetCategories
      .filter((category) => !isAboveTheLineCategory(category))
      .reduce((sum, category) => sum + getBudgetCategoryEstimate(category, budgetCategories), 0)
  );
};

export const getBelowTheLineActual = (budgetCategories: BudgetCategory[]): number => {
  return roundBudgetMoney(
    budgetCategories
      .filter((category) => !isAboveTheLineCategory(category))
      .reduce((sum, category) => sum + getBudgetCategoryActual(category, budgetCategories), 0)
  );
};

export const getBudgetTotalActual = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => {
  return roundBudgetMoney(
    budgetCategories.reduce(
      (sum, category) => sum + getBudgetCategoryActual(category, budgetCategories),
      0
    ) + (budgetContingency?.actual_amount ?? 0)
  );
};

export const getBudgetTotalRemaining = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => {
  return (
    getBudgetTotalEstimate(budgetCategories, budgetContingency) -
    getBudgetTotalActual(budgetCategories, budgetContingency)
  );
};

export const getBudgetTotalVariance = (
  budgetCategories: BudgetCategory[],
  budgetContingency: BudgetContingency | null
): number => roundBudgetMoney(getBudgetTotalRemaining(budgetCategories, budgetContingency));

export const getFringeAccountNumber = (
  categoryFringeAccountNumber: string,
  fringeIndex: number
) => {
  const categoryAccountNumberPrefix = categoryFringeAccountNumber.endsWith("-00")
    ? categoryFringeAccountNumber.slice(0, -3)
    : categoryFringeAccountNumber;
  const formattedIndex = `${(fringeIndex + 1).toString().padStart(2, "0")}`;
  return `${categoryAccountNumberPrefix}-${formattedIndex}`;
};

export const isFringeUnitTimeBased = (unit: BudgetFringeUnit): boolean => {
  return [BudgetFringeUnit.WEEKS, BudgetFringeUnit.DAYS, BudgetFringeUnit.HOURS].includes(unit);
};

const toFringeSummary = (
  amounts: BudgetFringeAmounts,
  details: Array<BudgetFringeAmounts & { fringe: BudgetFringeInput }>
) => ({
  estimate: amounts.estimate,
  actual_amount: amounts.actual,
  variance: amounts.actual ? roundBudgetMoney(amounts.estimate - amounts.actual) : 0,
  nested_fringes: details.map((detail) => {
    const fringe = detail.fringe as BudgetFringe;
    return {
      name: fringe.name,
      actual: detail.actual,
      estimate: detail.estimate,
      rate: fringe.rate,
      unit: fringe.unit,
      ceiling: fringe.ceiling,
      ceiling_type: fringe.ceiling_type,
    };
  }),
});

/**
 * Fringes for these items alone. A ceiling shared with items elsewhere in the budget is
 * applied as if these were the only items; use getCategoryFringesInBudget for a category.
 */
export const getFringesFromBudgetItems = (budgetItems: BudgetItem[]) => {
  const totals = calculateBudgetFringeTotals(getFringeEntries([budgetItems]), 1);
  return toFringeSummary(totals, totals.fringes);
};

/**
 * A category's fringes as its share of the budget-wide allocation, the same amounts as its
 * top sheet fringe row. A ceiling shared with other categories counts once per budget.
 * Lists the fringes linked in this category, in line order.
 */
export const getCategoryFringesInBudget = (
  category: BudgetCategory,
  allCategories: BudgetCategory[] = [category]
): ReturnType<typeof getFringesFromBudgetItems> => {
  const categoryIndex = allCategories.findIndex(
    (candidate) => candidate.budget_category_id === category.budget_category_id
  );
  if (categoryIndex === -1) return getCategoryFringesInBudget(category, [category]);
  const totals = getBudgetFringeTotals(allCategories);
  const detailsById = new Map(
    totals.fringes.map((detail) => [detail.fringe.budget_fringe_id, detail])
  );
  const linkedFringeIds = new Set(
    getFringeEntries([getAllItemsInCategory(category)]).flatMap((entry) =>
      (entry.fringes ?? []).map((fringe) => fringe.budget_fringe_id)
    )
  );
  const details = [...linkedFringeIds].flatMap((fringeId) => {
    const detail = detailsById.get(fringeId);
    return detail ? [{ fringe: detail.fringe, ...detail.categories[categoryIndex] }] : [];
  });
  return toFringeSummary(totals.categories[categoryIndex], details);
};

/**
 * Each account's share of the budget-wide fringes, in the order of
 * `categories.flatMap((category) => category.sub_categories)`. Ceilings apply once per budget,
 * and a category's accounts add up exactly to its share on the top sheet.
 */
export const getBudgetSubCategoryFringeShares = (
  categories: BudgetCategory[]
): BudgetFringeAmounts[] => {
  const subCategories = categories.flatMap((category) => category.sub_categories);
  const categoryIndexOfAccount = categories.flatMap((category, categoryIndex) =>
    category.sub_categories.map(() => categoryIndex)
  );
  // One entry group per account, then tagged with the category that account belongs to.
  const entries = getFringeEntries(subCategories.map((subCategory) => subCategory.items)).map(
    (entry) => ({
      ...entry,
      categoryIndex: categoryIndexOfAccount[entry.categoryIndex],
      accountIndex: entry.categoryIndex,
    })
  );
  return calculateBudgetFringeTotals(entries, categories.length, subCategories.length).accounts;
};

export const instanceOfBudgetContingency = (object: unknown): object is BudgetContingency => {
  return object !== null && typeof object === "object" && "contingency_percentage" in object;
};
